import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, Outlet, useLocation, matchPath } from 'react-router-dom';
import Sidebar from './Sidebar';
import Search from './Views/Search';
import SellerManagementPage from './Views/SellerManagement/SellerManagement';
import Marketing from './Views/Marketing/Marketing';
import AdminUsers from './Views/AdminUsers/AdminUsers';
import CategoryDetailPage from './Views/CategoryManagement/CategoryDetailPage';
import AllCategoriesPage from './Views/CategoryManagement/AllCategoriesPage';
import CatQnATest from '../Test/CatQnATest';
import BannedEmailsPage from './Views/VerificationRules/BannedEmailsPage';
import BannedPhonesPage from './Views/VerificationRules/BannedPhonesPage';
import BannedIPsPage from './Views/VerificationRules/BannedIPsPage';
import BannedPhrasesPage from './Views/VerificationRules/BannedPhrasesPage';
import ConditionDefinitionsPage from './Views/LeadPricingSetup/ConditionDefinitionsPage';
import CategoryGroupsPage from './Views/CategoryManagement/CategoryGroupsPage';
import FlagDefinitionsPage from './Views/CategoryManagement/FlagDefinitionsPage';
import CouponManagement from './Views/Payments/CouponManagement';
import ViewCoupons from './Views/Payments/ViewCoupons';
import CouponForm from './Views/Payments/CouponForm';
import MarketingLandingPageList from './Views/Marketing/MarketingLandingPage/MarketingLandingPageList';
import MarketingLandingPageForm from './Views/Marketing/MarketingLandingPage/MarketingLandingPageForm';
import SellerMarketingLandingPageList from './Views/Marketing/SellerMarketingLandingPage/SellerMarketingLandingPageList';
import SellerMarketingLandingPageForm from './Views/Marketing/SellerMarketingLandingPage/SellerMarketingLandingPageForm';
import CouponPermissionsPage from './Views/Payments/CouponPermissions';
import LeadDetailPage from './Views/Leads/LeadDetailPage';
import SalesCallLeads from './Views/Leads/SalesCallLeads';
import { useAdminStore } from '../../store/adminStore';
import LocaleSelector from '../../components/Admin/Category/LocaleSelector';
import SeoPageManager from './Views/SEO/SeoPageManager';
import SeoMediaTools from './Views/SEO/SeoMediaTools';
import SeoCategoryMetaManager from './Views/SEO/SeoCategoryMetaManager';
import LocationTagManager from './Views/SEO/LocationTagManager';
import LocationImageManager from './Views/SEO/LocationImageManager';
import StaticTasksManager from './Views/SEO/StaticTasksManager';
import SalesProspectCalculator from './Views/Prospects/SalesProspectCalculator';
import SearchProspect from './Views/Prospects/SearchProspect';
import ProspectScrapeTaskManager from './Views/Prospects/ProspectScrapeTaskManager';
import DirectoryDomainManager from './Views/Prospects/DirectoryDomainManager';
import ProspectSignupSettings from './Views/Prospects/ProspectSignupSettings';
import ProspectLlmReviewQueue from './Views/Prospects/ProspectLlmReviewQueue';
import ExternalCategoryMappings from './Views/Prospects/ExternalCategoryMappings';
import NotificationDefinitionsManager from './Views/Notifications/NotificationDefinitionsManager';
import BuyerDetailPage from './Views/Buyers/BuyerDetailPage';
import EmployeeReferralDashboard from './Views/Referrals/EmployeeReferralDashboard'; 
import { Menu } from 'lucide-react'; // Added for mobile menu icon
import CommissionPlansList from './Views/Referrals/CommissionPlansList'; // Assuming you create a standard table list view for this
import CommissionPlanForm from './Views/Referrals/CommissionPlanForm';
import AffiliateManagement from './Views/Referrals/AffiliateManagement';
import SystemToolsPage from './Views/SystemTools/SystemToolsPage';
// --- NEW: Import your permissions hook ---
import { usePermissions } from '../../hooks/usePermissions';

const ADMIN_TITLE_SUFFIX = 'Selyst Admin';

const toTitleCase = (value = '') =>
  value
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatLocaleTitle = (locale) =>
  locale ? `${locale.toUpperCase()} Categories` : 'Manage Categories';

const ADMIN_PAGE_TITLES = [
  { path: '/admin', title: 'Admin Panel | Sanctum' },
  { path: '/admin/search', title: 'Search Sellers / Buyers | Sanctum' },
  { path: '/admin/search-prospect', title: 'Search Prospect | Sanctum' },
  { path: '/admin/my-referrals', title: 'My Referral Dashboard | Sanctum' },
  { path: '/admin/prospects/prospect-calculator/:countryCode?/:categoryId?', title: 'Sales Prospect Calculator | Sanctum' },
  { path: '/admin/prospects/scrape-tasks', title: 'Prospect Scrape Tasks | Sanctum' },
  { path: '/admin/prospects/signup-settings', title: 'Signup Free Responses | Sanctum' },
  { path: '/admin/prospects/llm-review', title: 'LLM Review Queue | Sanctum' },
  { path: '/admin/prospects/category-mappings', title: 'External Category Mappings | Sanctum' },
  { path: '/admin/notifications/content', title: 'Notification Content | Sanctum' },
  { path: '/admin/sellers/:sellerProfileId/view/:tab?', title: null },
  { path: '/admin/lead/:leadId', title: 'Lead Details | Sanctum' },
  { path: '/admin/leads/prospect-leads', title: 'Prospect Call Leads | Sanctum' },
  { path: '/admin/buyers/:buyerId/view', title: 'Buyer Details | Sanctum' },
  { path: '/admin/cat-qna-test', title: 'Category Q&A Test | Sanctum' },
  { path: '/admin/affiliates/manage', title: 'Manage Affiliates | Sanctum' },
  { path: '/admin/referrals/commission-plans', title: 'Commission Plans | Sanctum' },
  { path: '/admin/referrals/commission-plans/new', title: 'New Commission Plan | Sanctum' },
  { path: '/admin/referrals/commission-plans/edit/:planId', title: 'Edit Commission Plan | Sanctum' },
  { path: '/admin/marketing', title: 'Marketing | Sanctum' },
  { path: '/admin/marketing/buyer-landing-pages', title: 'Buyer Landing Pages | Sanctum' },
  { path: '/admin/marketing/buyer-landing-pages/new', title: 'New Buyer Landing Page | Sanctum' },
  { path: '/admin/marketing/buyer-landing-pages/edit/:pageId', title: 'Edit Buyer Landing Page | Sanctum' },
  { path: '/admin/marketing/seller-landing-pages', title: 'Seller Landing Pages | Sanctum' },
  { path: '/admin/marketing/seller-landing-pages/new', title: 'New Seller Landing Page | Sanctum' },
  { path: '/admin/marketing/seller-landing-pages/edit/:pageId', title: 'Edit Seller Landing Page | Sanctum' },
  { path: '/admin/categories/manage', title: 'Manage Categories | Sanctum' },
  { path: '/admin/categories/manage/:locale', title: ({ locale }) => formatLocaleTitle(locale) },
  { path: '/admin/categories/manage/:locale/all-categories', title: ({ locale }) => `All ${formatLocaleTitle(locale)}` },
  { path: '/admin/categories/manage/:locale/view/:id/:tab?', title: ({ locale }) => `Category Details - ${formatLocaleTitle(locale)}` },
  { path: '/admin/categories/manage/:locale/groups', title: ({ locale }) => `Manage Category Groups - ${formatLocaleTitle(locale)}` },
  { path: '/admin/categories/flag-definitions', title: 'Flag Definitions | Sanctum' },
  { path: '/admin/lead-pricing-setup', title: 'Lead Pricing Setup | Sanctum' },
  { path: '/admin/lead-pricing-setup/condition-definitions', title: 'Condition Definitions | Sanctum' },
  { path: '/admin/users', title: 'Admin Users | Sanctum' },
  { path: '/admin/system-tools', title: 'System Tools | Sanctum' },
  { path: '/admin/verification-rules', title: 'Verification Rules | Sanctum' },
  { path: '/admin/verification-rules/emails', title: 'Banned Emails | Sanctum' },
  { path: '/admin/verification-rules/ips', title: 'Banned IPs | Sanctum' },
  { path: '/admin/verification-rules/phones', title: 'Banned Phones | Sanctum' },
  { path: '/admin/verification-rules/phrases', title: 'Banned Phrases | Sanctum' },
  { path: '/admin/payments', title: 'Payments | Sanctum' },
  { path: '/admin/payments/view/:status', title: ({ status }) => `${toTitleCase(status)} Coupons` },
  { path: '/admin/payments/create', title: 'Create Coupon | Sanctum' },
  { path: '/admin/payments/edit/:couponId', title: 'Edit Coupon | Sanctum' },
  { path: '/admin/payments/permissions', title: 'Coupon Activation Permissions | Sanctum' },
  { path: '/admin/seo', title: 'SEO Management | Sanctum' },
  { path: '/admin/seo/pages', title: 'SEO Pages | Sanctum' },
  { path: '/admin/seo/media-tools', title: 'SEO Media Tools | Sanctum' },
  { path: '/admin/seo/category-meta', title: 'Category Meta Content | Sanctum' },
  { path: '/admin/seo/location-tags', title: 'Location Tags | Sanctum' },
  { path: '/admin/seo/location-manager', title: 'Location Manager | Sanctum' },
  { path: '/admin/seo/static-tasks', title: 'Static Generators | Sanctum' },
];

const getAdminPageTitle = (pathname) => {
  const matchedRoute = ADMIN_PAGE_TITLES.find(({ path }) =>
    matchPath({ path, end: true }, pathname)
  );

  if (!matchedRoute) {
    return 'Admin Panel';
  }

  const match = matchPath({ path: matchedRoute.path, end: true }, pathname);
  if (!matchedRoute.title) {
    return null;
  }

  return typeof matchedRoute.title === 'function'
    ? matchedRoute.title(match?.params || {})
    : matchedRoute.title;
};

// --- NEW: Route Guard Component ---
const RequirePermission = ({ permission, redirectPath = "/admin/search" }) => {
  const { hasPermission } = usePermissions();
  
  if (!hasPermission(permission)) {
    return <Navigate to={redirectPath} replace />;
  }
  
  return <Outlet />;
};

const SyncUrlLocaleToStore = () => {
  const { locale: urlLocale } = useParams();
  const setLocale = useAdminStore((state) => state.setLocale);
  const storeLocale = useAdminStore((state) => state.locale);

  useEffect(() => {
    if (urlLocale && urlLocale !== storeLocale) {
      console.log(`Syncing URL locale '${urlLocale}' to store.`);
      setLocale(urlLocale);
    }
  }, [urlLocale, storeLocale, setLocale]);

  return null;
};

const CategoryLayout = () => {
  return (
    <>
      <SyncUrlLocaleToStore />
      <LocaleSelector />
      <Outlet />
    </>
  );
};

const SelectLocalePrompt = () => {
    const navigate = useNavigate();
    const currentLocale = useAdminStore((state) => state.context);

     useEffect(() => {
        if (currentLocale) {
            navigate(`/admin/categories/${currentLocale}`);
        }
     }, [currentLocale, navigate]);

    return (
        <div className="p-6">
            <LocaleSelector />
            <div className="mt-4 p-4 border rounded bg-card text-card-foreground">
                <p>Please select a locale (e.g., 'Master' or a country) to manage categories.</p>
            </div>
        </div>
    );
};

const AdminLanding = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const pageTitle = getAdminPageTitle(location.pathname);

    if (pageTitle) {
      document.title = `${pageTitle} | ${ADMIN_TITLE_SUFFIX}`;
    }
  }, [location.pathname]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white text-gray-900">
      
      {/* Sidebar Component with mobile state props */}
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      
      {/* Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {/* Mobile Header - Only visible on sm/md screens */}
        <header className="md:hidden flex items-center justify-between p-4 border-b border-gray-200 bg-white z-30">
          <div className="flex items-center gap-3">
            <button 
              onClick={toggleSidebar} 
              className="p-1 -ml-1 text-gray-600 hover:bg-gray-100 rounded focus:outline-none"
              aria-label="Open sidebar"
            >
              <Menu size={26} />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 m-0">Dashboard</h1>
          </div>
        </header>

        {/* Scrollable Main Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-[20px]">
          <Routes>
              <Route path="/" element={<Navigate to="/admin/search" />} />
              
              {/* Routes without explicit permissions in Sidebar (Public to all Admins) */}
              <Route path="sellers/:sellerProfileId/view/:tab?" element={<SellerManagementPage />} />
              <Route path="lead/:leadId" element={<LeadDetailPage />} />
              <Route path="buyers/:buyerId/view" element={<BuyerDetailPage />} />
              <Route path="cat-qna-test" element={<CatQnATest />} />

              {/* --- Protected Routes start here --- */}

              {/* users:read-only */}
              <Route element={<RequirePermission permission="users:read-only" />}>
                <Route path="search" element={<Search />} />
                <Route path="search-prospect" element={<SearchProspect />} />
                <Route path="my-referrals" element={<EmployeeReferralDashboard />} />
                <Route path="prospects/prospect-calculator/:countryCode?/:categoryId?" element={<SalesProspectCalculator />} />
                <Route path="prospects/scrape-tasks" element={<ProspectScrapeTaskManager />} />
                <Route path="prospects/directory-domains" element={<DirectoryDomainManager />} />
              </Route>
              {/* sales:view-leads-for-calling — masked real-lead browsing for the sales calling queue */}
              <Route element={<RequirePermission permission="sales:view-leads-for-calling" />}>
                <Route path="leads/prospect-leads" element={<SalesCallLeads />} />
              </Route>
              {/* admin_user:manage — signup free responses control real inventory given away */}
              <Route element={<RequirePermission permission="admin_user:manage" />}>
                <Route path="prospects/signup-settings" element={<ProspectSignupSettings />} />
                <Route path="prospects/llm-review" element={<ProspectLlmReviewQueue />} />
                <Route path="prospects/category-mappings" element={<ExternalCategoryMappings />} />
                <Route path="notifications/content" element={<NotificationDefinitionsManager />} />
                <Route path="system-tools" element={<SystemToolsPage />} />
              </Route>
              {/* affiliate:manage OR a new specific permission like commissions:manage */}
              <Route element={<RequirePermission permission="affiliate:manage" />}>
                <Route path="affiliates/manage" element={<AffiliateManagement />} />
                <Route path="referrals/commission-plans" element={<CommissionPlansList />} />
                <Route path="referrals/commission-plans/new" element={<CommissionPlanForm />} />
                <Route path="referrals/commission-plans/edit/:planId" element={<CommissionPlanForm />} />
              </Route>

              {/* marketing:landing-pages:manage */}
              <Route element={<RequirePermission permission="marketing:landing-pages:manage" />}>
                <Route path="marketing">
                  <Route index element={<Marketing />} />
                  <Route path="buyer-landing-pages" element={<MarketingLandingPageList />} />
                  <Route path="buyer-landing-pages/new" element={<MarketingLandingPageForm />} />
                  <Route path="buyer-landing-pages/edit/:pageId" element={<MarketingLandingPageForm />} />
                  <Route path="seller-landing-pages" element={<SellerMarketingLandingPageList />} />
                  <Route path="seller-landing-pages/new" element={<SellerMarketingLandingPageForm />} />
                  <Route path="seller-landing-pages/edit/:pageId" element={<SellerMarketingLandingPageForm />} />
                </Route>
              </Route>

              {/* content:categories:manage */}
              <Route element={<RequirePermission permission="content:categories:manage" />}>
                <Route path="categories">
                  <Route path="manage" element={<SelectLocalePrompt />} />
                    <Route path="manage/:locale" element={<CategoryLayout />}>
                        <Route index element={<CategoryDetailPage />} />
                        <Route path="all-categories" element={<AllCategoriesPage />} />
                        <Route path="view/:id/:tab?" element={<CategoryDetailPage />} />
                        <Route path="groups" element={<CategoryGroupsPage/>} />
                    </Route>
                  <Route path="flag-definitions" element={<FlagDefinitionsPage />} />
                </Route>
                <Route path="lead-pricing-setup">
                  <Route path="condition-definitions" element={<ConditionDefinitionsPage />} />
                  <Route index element={<Navigate to="condition-definitions" />} />
                </Route>
              </Route>

              {/* admin_user:view */}
              <Route element={<RequirePermission permission="admin_user:view" />}>
                <Route path="users" element={<AdminUsers />} />
              </Route>

              {/* cx:update-banned-content */}
              <Route element={<RequirePermission permission="cx:update-banned-content" />}>
                <Route path="verification-rules">
                  <Route path="emails" element={<BannedEmailsPage /> }/>
                  <Route path="ips" element={<BannedIPsPage />} /> 
                  <Route path="phones" element={<BannedPhonesPage />} />
                  <Route path="phrases" element={<BannedPhrasesPage />} />
                  <Route index element={<Navigate to="emails" />} />
                </Route>
              </Route>

              {/* Payments - Nested permissions */}
              <Route element={<RequirePermission permission="coupons:manage" />}>
                <Route path="payments" element={<CouponManagement />}>
                  <Route index element={<Navigate to="view/active" />} />
                  
                  <Route element={<RequirePermission permission="coupons:view" />}>
                    <Route path="view/:status" element={<ViewCoupons />} />
                  </Route>
                  
                  <Route element={<RequirePermission permission="coupons:create-modify" />}>
                    <Route path="create" element={<CouponForm />} />
                    <Route path="edit/:couponId" element={<CouponForm />} />
                  </Route>
                  
                  <Route element={<RequirePermission permission="coupons:assign-admin-user" />}>
                    <Route path="permissions" element={<CouponPermissionsPage />} />
                  </Route>
                </Route>
              </Route>

              {/* content:seo:manage */}
              <Route element={<RequirePermission permission="content:seo:manage" />}>
                <Route path="seo">
                  <Route index element={<Navigate to="pages" />} />
                  <Route path="pages" element={<SeoPageManager />} />
                  <Route path="media-tools" element={<SeoMediaTools />} />
                  <Route path="category-meta" element={<SeoCategoryMetaManager />} />
                  <Route path="location-tags" element={<LocationTagManager />} />
                  <Route path="location-manager" element={<LocationImageManager />} />
                  <Route path="static-tasks" element={<StaticTasksManager />} />
                </Route>
              </Route>
              
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminLanding;
